import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import AddAssetDialog from '../components/AddAssetDialog';
import { useApp } from '../context/AppContext';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Trash2, X } from 'lucide-react';
import { useSort, SortableTh } from '../components/SortableTable';
import TableSearch from '../components/TableSearch';

export default function AssetMaster() {
  const { assets, sources, holdings, deleteAsset } = useApp();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const { sort, onSort, sortRows } = useSort({ key: 'investmentName', dir: 'asc' });

  // Usage count by investment name (assetId removed)
  const usageByName = useMemo(() => {
    const m = {};
    Object.values(holdings).forEach((bySrc) => {
      Object.values(bySrc).forEach((rows) => {
        rows.forEach((r) => {
          const name = r.investmentName;
          if (!name) return;
          m[name] = (m[name] || 0) + 1;
        });
      });
    });
    return m;
  }, [holdings]);

  const sourceNameById = Object.fromEntries(sources.map((s) => [s.id, s.name]));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      a.investmentName.toLowerCase().includes(q) ||
      (a.institutions || []).some((i) => i.toLowerCase().includes(q)) ||
      (a.geography || '').toLowerCase().includes(q) ||
      (a.mainAssetClass || '').toLowerCase().includes(q) ||
      (a.subAssetClass || '').toLowerCase().includes(q)
    );
  }, [assets, query]);

  const sorted = sortRows(filtered, {
    investmentName: (a) => a.investmentName,
    mainAssetClass: (a) => a.mainAssetClass,
    subAssetClass: (a) => a.subAssetClass,
    geography: (a) => a.geography,
    institutions: (a) => (a.institutions || []).join(', '),
    sources: (a) => (a.sources || []).map((sid) => sourceNameById[sid] || sid).join(', '),
    rows: (a) => usageByName[a.investmentName] || 0,
  });

  // Strict guard: do not allow deleting an asset that's referenced anywhere in holdings.
  const isEligible = (a) => (usageByName[a.investmentName] || 0) === 0;
  const eligibleVisible = sorted.filter(isEligible);
  const eligibleVisibleIds = eligibleVisible.map((a) => a.id);
  const allEligibleSelected = eligibleVisibleIds.length > 0 && eligibleVisibleIds.every((id) => selected.has(id));
  const someEligibleSelected = eligibleVisibleIds.some((id) => selected.has(id));

  const toggleOne = (id) => {
    const a = assets.find((x) => x.id === id);
    if (a && !isEligible(a)) {
      toast.error(`Cannot delete "${a.investmentName}": in use across ${usageByName[a.investmentName]} holding row${usageByName[a.investmentName] === 1 ? '' : 's'}. Clear those rows from Data Entry first.`);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      if (allEligibleSelected) {
        const next = new Set(prev);
        eligibleVisibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...eligibleVisibleIds]);
    });
  };

  const clearSelection = () => setSelected(new Set());

  const deleteOne = async (a) => {
    if (!isEligible(a)) {
      toast.error(`Cannot delete "${a.investmentName}": in use across ${usageByName[a.investmentName]} holding row${usageByName[a.investmentName] === 1 ? '' : 's'}. Clear those rows from Data Entry first.`);
      return;
    }
    if (!confirm(`Delete asset "${a.investmentName}"?`)) return;
    try {
      await deleteAsset(a.id);
      setSelected((prev) => { const next = new Set(prev); next.delete(a.id); return next; });
      toast.success(`Deleted ${a.investmentName}`);
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const items = ids.map((id) => assets.find((a) => a.id === id)).filter(Boolean);
    // Defensive: in case selection somehow contains in-use assets, drop them with a notice.
    const blocked = items.filter((a) => !isEligible(a));
    const allowed = items.filter(isEligible);
    if (blocked.length) {
      toast.error(`Skipping ${blocked.length} in-use asset${blocked.length === 1 ? '' : 's'}: ${blocked.slice(0, 3).map((a) => a.investmentName).join(', ')}${blocked.length > 3 ? '…' : ''}`);
    }
    if (allowed.length === 0) {
      clearSelection();
      return;
    }
    const namesPreview = allowed.map((a) => a.investmentName).slice(0, 10).join(', ');
    if (!confirm(`Delete ${allowed.length} asset${allowed.length === 1 ? '' : 's'}?\n\n${namesPreview}${allowed.length > 10 ? `\n…and ${allowed.length - 10} more` : ''}`)) return;

    setBusy(true);
    let ok = 0, fail = 0;
    for (const a of allowed) {
      try { await deleteAsset(a.id); ok += 1; } catch (_) { fail += 1; }
    }
    setBusy(false);
    clearSelection();
    if (ok) toast.success(`Deleted ${ok} asset${ok === 1 ? '' : 's'}`);
    if (fail) toast.error(`Failed to delete ${fail}`);
  };

  return (
    <>
      <PageHeader
        overline="Masters"
        title="Asset Master"
        description="Each asset is unique by its Investment Name. Delete is allowed only when an asset has zero holdings rows — in-use assets are blocked to protect data integrity."
        actions={
          <button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-asset-button">
            <Plus size={14} /> Add asset
          </button>
        }
      />

      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by investment name, institution, geography or class…"
        testId="asset-search"
        stats={`${sorted.length} / ${assets.length} assets`}
      />

      {/* Selection action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-2 border border-[hsl(var(--primary))] bg-[#F1F6F2]" data-testid="bulk-action-bar">
          <span className="font-heading font-semibold text-[13px] text-[hsl(var(--primary))]">
            {selected.size} selected
          </span>
          <span className="text-[11px] font-mono text-[#575757]">
            ({eligibleVisible.length} eligible visible)
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="btn-secondary !py-1 !px-2.5 text-[11px]"
              data-testid="clear-selection"
            >
              <X size={11} /> Clear
            </button>
            <button
              onClick={deleteSelected}
              disabled={busy}
              className="btn-primary !py-1 !px-3 text-[11px] !bg-[#9B2C2C] hover:!bg-[#7A2222] disabled:opacity-50"
              data-testid="delete-selected"
            >
              <Trash2 size={12} /> {busy ? 'Deleting…' : `Delete ${selected.size}`}
            </button>
          </div>
        </div>
      )}

      <div className="border border-border bg-white overflow-x-auto">
        <table className="grid-table" data-testid="asset-master-table">
          <thead>
            <tr>
              <th className="w-[36px] text-center">
                <Checkbox
                  checked={allEligibleSelected}
                  onCheckedChange={toggleAllVisible}
                  disabled={eligibleVisibleIds.length === 0}
                  className={`border-[#B0B0A8] data-[state=checked]:bg-[hsl(var(--primary))] data-[state=checked]:border-[hsl(var(--primary))] ${someEligibleSelected && !allEligibleSelected ? 'opacity-70' : ''}`}
                  data-testid="select-all-visible"
                />
              </th>
              <SortableTh sortKey="investmentName" sort={sort} onSort={onSort}>Investment Name</SortableTh>
              <SortableTh sortKey="mainAssetClass" sort={sort} onSort={onSort}>Main Asset Class</SortableTh>
              <SortableTh sortKey="subAssetClass" sort={sort} onSort={onSort}>Sub-Asset Class</SortableTh>
              <SortableTh sortKey="geography" sort={sort} onSort={onSort}>Geography</SortableTh>
              <SortableTh sortKey="institutions" sort={sort} onSort={onSort}>Institutions</SortableTh>
              <SortableTh sortKey="sources" sort={sort} onSort={onSort}>Sources</SortableTh>
              <SortableTh sortKey="rows" sort={sort} onSort={onSort} numeric>Rows</SortableTh>
              <th className="w-[56px]" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((a) => {
              const usage = usageByName[a.investmentName] || 0;
              const eligible = isEligible(a);
              const isSelected = selected.has(a.id);
              const blockedTitle = `Cannot delete — in use across ${usage} holding row${usage === 1 ? '' : 's'}. Clear those rows from Data Entry first.`;
              return (
                <tr key={a.id} className={isSelected ? 'bg-[#F1F6F2]' : ''}>
                  <td className="text-center">
                    {eligible ? (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleOne(a.id)}
                        className="border-[#B0B0A8] data-[state=checked]:bg-[hsl(var(--primary))] data-[state=checked]:border-[hsl(var(--primary))]"
                        data-testid={`select-asset-${a.id}`}
                      />
                    ) : (
                      <span
                        className="inline-flex w-4 h-4 border border-[#DDD] bg-[#F6F6F4] cursor-not-allowed rounded-sm"
                        title={blockedTitle}
                        onClick={() => toast.error(blockedTitle)}
                        data-testid={`select-asset-${a.id}-blocked`}
                      />
                    )}
                  </td>
                  <td className="font-heading font-semibold">{a.investmentName}</td>
                  <td><span className="chip chip-neutral">{a.mainAssetClass || '—'}</span></td>
                  <td><span className="chip chip-neutral">{a.subAssetClass || '—'}</span></td>
                  <td><span className="chip chip-neutral">{a.geography || '—'}</span></td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(a.institutions || []).length === 0
                        ? <span className="text-[11px] text-[#AAA]">—</span>
                        : a.institutions.map((inst) => <span key={inst} className="chip">{inst}</span>)}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(a.sources || []).length === 0
                        ? <span className="text-[11px] text-[#AAA]">—</span>
                        : a.sources.map((sid) => (
                            <span key={sid} className="chip chip-neutral">{sourceNameById[sid] || sid}</span>
                          ))}
                    </div>
                  </td>
                  <td className="num tabular-nums">{usage}</td>
                  <td>
                    {eligible ? (
                      <button
                        onClick={() => deleteOne(a)}
                        className="btn-ghost text-[#9B2C2C] hover:bg-[#F5E4E4]"
                        title="Delete this asset"
                        data-testid={`delete-asset-${a.investmentName}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    ) : (
                      <button
                        onClick={() => toast.error(blockedTitle)}
                        className="text-[#CCC] cursor-not-allowed inline-flex p-1.5"
                        title={blockedTitle}
                        data-testid={`delete-asset-${a.investmentName}-blocked`}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-12 text-[#AAA]">No matches</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddAssetDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
